/**
 * #1101 — "a background database operation ended": the ONE per-file signal the server sends to the PERSON whose request
 * started a db operation it finished out of band (after the request answered). The DB editor turns it into a notice;
 * a bell notification (`db_operation_ended`) carries the same facts for someone who left the editor.
 *
 * Generic on purpose: the first operation is a large standalone db's background version save (#1074 item 6) that ends
 * without recording a version; #1090's app-db Replace reports its outcomes through the same channel. A new operation or
 * outcome is a new member of these unions plus its copy (the editor's `database.operationEnded.*` keys in every locale,
 * and the notification's English builder) — never a second channel.
 *
 * It names the file by the id the person's REQUEST used (a `.shared` / `.chain` route stays a route), never the
 * resolved target's id in another org.
 */

/** The background operation that ended. `replace` (#1090): a large Replace of an app's live database, run by a job. */
export type DbOperationKind = 'version-save' | 'replace'

/**
 * How it ended.
 * - `not-saved` (version-save): the change is in the database, but no drive version of it was recorded;
 * - `replaced` (replace): the database now holds the uploaded one; its previous contents are a version;
 * - `not-applied` (replace): the database was left unchanged;
 * - `failed` (replace): the Replace could not be finished and waits for an operator (`stuck`) — nothing was deleted.
 */
export type DbOperationOutcome = 'not-saved' | 'replaced' | 'not-applied' | 'failed'

/**
 * Why, when it matters to the person:
 * - `too-large`: the database could not be copied within the time a version save may take;
 * - `superseded` (version-save): a newer version of the database was saved in the drive while the change was being
 *   recorded (a live mirror intent, E7 PR 2 slice 2) — the change was not saved, and the drive's version replaces it;
 * - `refused`: every attempt to record it was refused (a full drive, access changed meanwhile, the db layer down) —
 *   for a Replace: the act's re-check or a version gate refused it;
 * - `invalid` (replace): the uploaded file is not a sound SQLite database;
 * - `busy` (replace): the database stayed busy;
 * - `gone` (replace): the database was deleted, moved, or is no longer an app's database;
 * - `stuck` (replace): the Replace was interrupted in a state only an operator may resolve;
 * - `error` (replace): something unexpected went wrong before the database was touched.
 */
export type DbOperationReason = 'too-large' | 'superseded' | 'refused' | 'invalid' | 'busy' | 'gone' | 'stuck' | 'error'
  // #1090 — `damaged`: replaced, but the old contents could not be read (not in its history); `resolved`: an
  // administrator ended a paused Replace.
  | 'damaged' | 'resolved'

export interface DbOperationEnded {
  /** The db as the person's request named it. */
  fileId: string
  operation: DbOperationKind
  outcome: DbOperationOutcome
  reason?: DbOperationReason
}

/** What each operation may report: its outcomes, and the reasons that go with them. A pair outside it is not this
 *  build's — the parser drops it (a `version-save` that "replaced", a `replace` that "wasn't saved"). */
const SHAPES: Record<DbOperationKind, { outcomes: readonly DbOperationOutcome[]; reasons: readonly DbOperationReason[] }> = {
  'version-save': { outcomes: ['not-saved'], reasons: ['too-large', 'superseded', 'refused'] },
  replace: { outcomes: ['replaced', 'not-applied', 'failed'], reasons: ['refused', 'invalid', 'busy', 'gone', 'stuck', 'error', 'damaged', 'resolved'] },
}

/** Parse an untrusted wire value (a realtime frame, a notification's data) — null unless every field is known. */
export function parseDbOperationEnded(v: unknown): DbOperationEnded | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.fileId !== 'string' || !o.fileId) return null
  const shape = typeof o.operation === 'string' && Object.hasOwn(SHAPES, o.operation) ? SHAPES[o.operation as DbOperationKind] : null
  if (!shape) return null
  if (!shape.outcomes.includes(o.outcome as DbOperationOutcome)) return null
  if (o.reason !== undefined && !shape.reasons.includes(o.reason as DbOperationReason)) return null
  return {
    fileId: o.fileId,
    operation: o.operation as DbOperationKind,
    outcome: o.outcome as DbOperationOutcome,
    ...(o.reason !== undefined ? { reason: o.reason as DbOperationReason } : {}),
  }
}
